import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, PenLine } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage, endpoints } from "@/api/client";
import { ConservazioneStato } from "@/components/ConservazioneStato";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CenteredLoader, ErrorState } from "@/components/ui/feedback";
import {
  firDettaglioDataEmissione,
  firDettaglioNumero,
  firRifiutoCampi,
  firSoggettiCampi,
  firNeedsSign,
  firStatoLabel,
  firStatoVariant,
  firTabHasData,
  firTrasportoCampi,
  type FirRecord,
  type FirTabId,
} from "@/lib/fir";
import { cn } from "@/lib/utils";

const TABS: { id: FirTabId; label: string }[] = [
  { id: "rifiuto", label: "Rifiuto" },
  { id: "soggetti", label: "Soggetti" },
  { id: "trasporto", label: "Trasporto" },
];

function CampiGrid({ campi }: { campi: { label: string; value: string }[] }) {
  return (
    <dl className="grid gap-4 sm:grid-cols-2">
      {campi.map(({ label, value }) => (
        <div key={label} className="space-y-1">
          <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
          <dd className="text-sm break-words whitespace-pre-wrap">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function FirDettaglioDialog({
  numeroFir,
  open,
  onOpenChange,
  onFirma,
}: {
  numeroFir: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Apre il flusso firma XFIR (PIN) se lo stato lo consente. */
  onFirma?: (numeroFir: string) => void;
}) {
  const [tab, setTab] = useState<FirTabId>("rifiuto");
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["fir-dettaglio", numeroFir],
    queryFn: () => endpoints.formulari.get(encodeURIComponent(numeroFir!)),
    enabled: open && !!numeroFir,
  });

  const archivesQ = useQuery({
    queryKey: ["archive", "xfir", numeroFir],
    queryFn: () =>
      endpoints.archive.list({
        doc_kind: "xfir_xml",
        reference_id: numeroFir!,
        limit: 5,
      }),
    enabled: open && !!numeroFir,
  });

  const archiveMut = useMutation({
    mutationFn: () => endpoints.archive.snapshotXfir(numeroFir!),
    onSuccess: () => {
      toast.success("xFIR archiviato su cloud");
      qc.invalidateQueries({ queryKey: ["archive", "xfir", numeroFir] });
    },
    onError: (e) => {
      toast.error("Archivio xFIR non riuscito", { description: apiErrorMessage(e) });
    },
  });

  const d = (q.data ?? null) as FirRecord | null;
  const latestArchive = archivesQ.data?.[0];

  useEffect(() => {
    if (open) setTab("rifiuto");
  }, [open, numeroFir]);

  const campiTab = useMemo(() => {
    if (!d) return [];
    switch (tab) {
      case "rifiuto":
        return firRifiutoCampi(d);
      case "soggetti":
        return firSoggettiCampi(d);
      case "trasporto":
        return firTrasportoCampi(d);
      default:
        return [];
    }
  }, [d, tab]);

  if (!numeroFir) return null;

  const stato = d ? firStatoLabel(d) : "";
  const statoRaw = d ? String(d.stato_formulario ?? d.stato ?? "") : "";
  const canFirma = !!onFirma && firNeedsSign(statoRaw);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(90vh,720px)] max-w-3xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="space-y-3 border-b px-6 py-4 text-left">
          <DialogTitle className="text-base font-semibold">
            Informazioni FIR — {numeroFir}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Dettaglio anagrafico e dati operativi del formulario FIR {numeroFir}.
          </DialogDescription>
          {d && (
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="font-mono text-xs font-normal">
                {firDettaglioNumero(d)}
              </Badge>
              <Badge variant={firStatoVariant(stato)} className="text-xs font-normal">
                {stato}
              </Badge>
              <span className="text-xs text-muted-foreground">
                Data emissione{" "}
                <span className="font-medium text-foreground">{firDettaglioDataEmissione(d)}</span>
              </span>
            </div>
          )}
        </DialogHeader>

        {q.isLoading && (
          <div className="px-6 py-8">
            <CenteredLoader />
          </div>
        )}
        {q.error && (
          <div className="px-6 py-4">
            <ErrorState error={q.error} />
          </div>
        )}

        {d && !q.isLoading && (
          <>
            <div className="flex flex-wrap gap-1 border-b px-4 pt-2">
              {TABS.map((t) => {
                const enabled = firTabHasData(d, t.id);
                const active = tab === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    disabled={!enabled}
                    onClick={() => enabled && setTab(t.id)}
                    className={cn(
                      "rounded-t-md px-3 py-2 text-xs font-medium transition-colors",
                      active && enabled
                        ? "border-b-2 border-primary text-foreground"
                        : "text-muted-foreground hover:text-foreground",
                      !enabled && "cursor-not-allowed opacity-40",
                    )}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
              <CampiGrid campi={campiTab} />
            </div>
          </>
        )}

        <DialogFooter className="border-t px-6 py-3 sm:justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {latestArchive?.status === "archived" && (
              <Badge variant="success">Archiviato</Badge>
            )}
            {latestArchive && <ConservazioneStato doc={latestArchive} />}
            {latestArchive?.status === "pending" && (
              <Badge variant="outline">Archivio in corso</Badge>
            )}
            {latestArchive?.status === "failed" && (
              <Badge variant="destructive">Archivio errore</Badge>
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={archiveMut.isPending || !numeroFir}
              onClick={() => archiveMut.mutate()}
            >
              <Archive className="mr-1 h-3.5 w-3.5" />
              Archivia xFIR
            </Button>
            {latestArchive?.status === "archived" && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={async () => {
                  try {
                    const safe = (numeroFir ?? "fir").replace(/[^\w.\-]+/g, "_");
                    await endpoints.archive.downloadFile(
                      latestArchive.id,
                      `${safe}.xfir`,
                    );
                  } catch (e) {
                    toast.error("Download non riuscito", {
                      description: apiErrorMessage(e),
                    });
                  }
                }}
              >
                Scarica xFIR
              </Button>
            )}
          </div>
          {canFirma && (
            <Button
              type="button"
              onClick={() => onFirma!(numeroFir)}
            >
              <PenLine className="mr-1.5 h-4 w-4" /> Firma XFIR
            </Button>
          )}
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Chiudi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
